import React from 'react';
import { AlertTriangle, FileWarning, BellOff, BarChart3, TrendingUp, ShieldCheck, Users, Bell } from 'lucide-react';
import { useReveal } from '../../hooks/useReveal';
import { pastel } from '../../lib/palette';

const problemas = [
  { icon: FileWarning, text: 'Registros dispersos y poco estandarizados.', tone: pastel.coral },
  { icon: AlertTriangle, text: 'Acciones reactivas cuando la situación ya escaló.', tone: pastel.amber },
  { icon: BellOff, text: 'Falta de trazabilidad y seguimiento de casos.', tone: pastel.pink },
  { icon: BarChart3, text: 'Poca información para apoyar decisiones directivas.', tone: pastel.indigo },
];

const oportunidades = [
  { icon: TrendingUp, text: 'Detección temprana de señales de riesgo.', tone: pastel.mint },
  { icon: ShieldCheck, text: 'Gestión ordenada con evidencia y responsables.', tone: pastel.violet },
  { icon: Bell, text: 'Alertas, seguimiento y análisis por curso o estudiante.', tone: pastel.sky },
  { icon: Users, text: 'Mejor convivencia y mayor resguardo institucional.', tone: pastel.teal },
];

const Problema = () => {
  const head = useReveal();
  return (
    <section id="problema" data-testid="problema-section" className="relative py-14 sm:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div ref={head.ref} className="reveal max-w-3xl">
          <div className="chip" data-testid="problema-chip">Problema y oportunidad</div>
          <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-brand-deep tracking-tight leading-tight">
            De una gestión reactiva a una{' '}
            <span className="text-brand-violet">prevención inteligente y trazable.</span>
          </h2>
          <p className="mt-5 text-lg text-brand-muted leading-relaxed">
            Los equipos directivos y de convivencia necesitan información clara, registros ordenados y
            señales tempranas. PreventiApp transforma lo que el colegio ya observa en información útil
            para actuar a tiempo.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Problemas frecuentes */}
          <article data-testid="problemas-card" className="card-soft p-7 sm:p-9">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-2xl bg-[#FEF1ED] text-[#E74C3C] flex items-center justify-center">
                <AlertTriangle className="w-6 h-6" />
              </span>
              <h3 className="text-xl sm:text-2xl font-bold text-brand-deep">Problemas frecuentes</h3>
            </div>
            <ul className="mt-6 space-y-4">
              {problemas.map((p) => (
                <li key={p.text} data-testid={`problema-item-${p.text.slice(0, 12)}`} className="flex items-start gap-3">
                  <span className="mt-0.5 w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: p.tone.bg, color: p.tone.fg }}>
                    <p.icon className="w-4 h-4" />
                  </span>
                  <p className="text-base text-brand-deep leading-relaxed">{p.text}</p>
                </li>
              ))}
            </ul>
          </article>

          {/* Oportunidades */}
          <article data-testid="oportunidades-card" className="card-soft p-7 sm:p-9 relative overflow-hidden">
            <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-[#EEF4FF] opacity-80" aria-hidden />
            <div className="relative">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-2xl bg-brand-violet text-white flex items-center justify-center">
                  <TrendingUp className="w-6 h-6" />
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-brand-deep">Oportunidad de mejora</h3>
              </div>
              <ul className="mt-6 space-y-4">
                {oportunidades.map((p) => (
                  <li key={p.text} data-testid={`oportunidad-item-${p.text.slice(0, 12)}`} className="flex items-start gap-3">
                    <span className="mt-0.5 w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: p.tone.bg, color: p.tone.fg }}>
                      <p.icon className="w-4 h-4" />
                    </span>
                    <p className="text-base text-brand-deep leading-relaxed">{p.text}</p>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        </div>

        <p className="mt-10 text-center text-base sm:text-lg text-brand-muted">
          <span className="font-bold text-brand-deep">PreventiApp</span> transforma observaciones cotidianas en información útil{' '}
          <span className="font-bold text-brand-violet">para actuar a tiempo.</span>
        </p>
      </div>
    </section>
  );
};

export default Problema;
